import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Label,
  DropdownItem,
  ListGroupItem,
  Toggle,
  LoadingSpinner,
  addToast
} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import sessionStore from 'client/Session/SessionStore';
import classnames from 'classnames';
import {pluralize} from 'lib/stringUtils';

import './global-assignments.scss';

import type {GlobalAssignmentFilterObj} from '../constants';

import {classesString} from './AssignmentCard.react';
import {getClassesQuery} from './GlobalAssignments.queries';

interface AssignmentFiltersProps {
  filters: GlobalAssignmentFilterObj;
  setFilters: (value: any) => void;
  isDraftsTab?: boolean;
  setFiltersToUse?: (value: any) => void;
  isMobile?: boolean;
}

interface ArchivedFooterProps {
  showArchived: boolean;
  setClassesPage: (page: number) => void;
  setShowArchived: (value: boolean) => void;
  classesLoading: boolean;
  toggleShowArchivedFirstTime: boolean | null;
  setToggleShowArchivedFirstTime: (value: boolean | null) => void;
}

const ArchivedFooter = ({
  showArchived,
  setClassesPage,
  setShowArchived,
  classesLoading,
  toggleShowArchivedFirstTime,
  setToggleShowArchivedFirstTime
}: ArchivedFooterProps) => {
  return (
    <>
      <span className='mr-x1'>Show archived</span>
      <Toggle
        aria-label='Show archived'
        checked={showArchived}
        onChange={() => {
          setClassesPage(1);
          setShowArchived(!showArchived);
          if (toggleShowArchivedFirstTime === false) {
            setToggleShowArchivedFirstTime(true);
          }
        }}
        disabled={classesLoading}
      />
    </>
  );
};

const ClassFilter = ({
  setFilters,
  filters,
  isDraftsTab,
  setFiltersToUse,
  isMobile
}: AssignmentFiltersProps) => {
  const [classesLoading, setClassesLoading] = useState<boolean>(false);
  const [classes, setClasses] = useState<ClassroomModelV1[]>([]);
  const [classesPage, setClassesPage] = useState<number>(1);
  const [totalNumPagesClasses, setTotalNumPagesClasses] = useState<number>(0);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [toggleShowArchivedFirstTime, setToggleShowArchivedFirstTime] = useState<boolean | null>(
    false
  );

  const onClickFilterOption = useCallback(
    (classroom: {id: string; name: string}) => {
      const nextClasses = {...filters.classes};
      // Key selection off presence, not the stored name (a class could be named '').
      if (classroom.id in nextClasses) {
        delete nextClasses[classroom.id];
      } else {
        nextClasses[classroom.id] = classroom.name;
      }
      setFilters({...filters, classes: nextClasses});
      if (isDraftsTab && setFiltersToUse) {
        setFiltersToUse({...filters, classes: nextClasses});
      }
    },
    [filters, setFilters, isDraftsTab, setFiltersToUse]
  );

  const fetchClasses = useCallback(
    async ({pageNum} = {pageNum: 1}) => {
      try {
        setClassesLoading(true);
        const isAdmin = sessionStore.isSchoolAdministrator();
        let totalNumPages = 1;
        const allClassesQuery = getClassesQuery({
          includeArchived: showArchived,
          isTeacher: isDraftsTab ? true : isAdmin,
          page: pageNum
        });
        const allClasses = await allClassesQuery.getResourcePromise();
        totalNumPages = allClassesQuery.getResourceMetadata().getIn(['page', 'total_pages'], 0);
        setTotalNumPagesClasses(totalNumPages);
        if (pageNum === 1) {
          setClasses(allClasses.toArray());
        } else {
          setClasses((prevClasses) => [...prevClasses, ...allClasses.toArray()]);
        }
        setClassesLoading(false);
        if (toggleShowArchivedFirstTime === true) {
          setToggleShowArchivedFirstTime(null);
        }
      } catch {
        setClassesLoading(false);
        if (toggleShowArchivedFirstTime === true) {
          setToggleShowArchivedFirstTime(null);
        }
        addToast({
          color: 'negative',
          title: "Couldn't fetch classes",
          message: 'Error fetching classes.'
        });
      }
    },
    [showArchived, isDraftsTab, toggleShowArchivedFirstTime, setToggleShowArchivedFirstTime]
  );

  const handleScrollClasses = useCallback(
    (e) => {
      if (!classesLoading) {
        const {scrollHeight, scrollTop, clientHeight} = e.target;
        const nearBottom = scrollHeight - clientHeight - scrollTop < 100;

        if (nearBottom && classesPage < totalNumPagesClasses) {
          setClassesLoading(true);
          fetchClasses({pageNum: classesPage + 1});
          setClassesPage((prevPage) => prevPage + 1);
        }
      }
    },
    [fetchClasses, classesPage, totalNumPagesClasses, classesLoading]
  );

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- effect intentionally kicks off a data fetch; the synchronous setState publishes its loading/reset state
    setClassesPage(1);
    fetchClasses();
  }, [fetchClasses]);

  const numClassesSelected = Object.keys(filters.classes).length;

  return (
    <>
      {!isDraftsTab && (
        <div className='mb-x1 flex items-center justify-between'>
          <Label>Classes</Label>
          {!!Object.keys(filters.classes).length && (
            <button
              className='global-assignments__filters-clear content-tertiary'
              type='button'
              disabled={classesLoading}
              onClick={() =>
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  classes: {}
                }))
              }
            >
              Clear
            </button>
          )}
        </div>
      )}
      <Dropdown
        className={classnames({
          'global-assignments__filters-drafts-class': isDraftsTab
        })}
        trigger={
          <Button
            className={isDraftsTab ? '' : 'font-normal'}
            color={isDraftsTab && numClassesSelected ? 'primary' : 'secondary'}
            variant='outlined'
            aria-label='Classes'
            data-testid='class-trigger'
          >
            <div className='truncate'>
              {isDraftsTab && numClassesSelected
                ? `${numClassesSelected} ${pluralize(
                    'Class',
                    numClassesSelected,
                    'Classes'
                  )} Selected`
                : ''}
              {isDraftsTab && !numClassesSelected ? `${!isMobile ? 'Filter by' : ''} Class` : ''}
              {!isDraftsTab && classesString(filters.classes)}
            </div>
            <Icon
              icon='caret-down'
              className={classnames({
                'ml-x1': isDraftsTab
              })}
            />
          </Button>
        }
        position='bottom-start'
        withMaxHeight
        trayProps={
          !isMobile
            ? {
                modifiers: {
                  flip: {
                    enabled: false
                  }
                }
              }
            : undefined
        }
      >
        <div className='m-dropdown__content--max relative' onScroll={handleScrollClasses}>
          <>
            {classesLoading && toggleShowArchivedFirstTime && (
              <div className='mt-x2 flex justify-center'>
                <LoadingSpinner size={2} />
              </div>
            )}
            {!toggleShowArchivedFirstTime && (
              <>
                {classes.map((c) => (
                  <DropdownItem
                    bold={false}
                    key={c.id}
                    onClick={() => onClickFilterOption(c)}
                    className={classnames('global-assignments__class', {
                      'global-assignments__archived-class-entry': c.status === 'archived'
                    })}
                    data-testid={`${c.id}-option`}
                    color={c.status === 'archived' ? 'inherit' : 'black'}
                  >
                    <div className='truncate'>
                      <Icon
                        icon='check'
                        className={classnames('fa-fw mr-x2', {
                          'opacity-100': filters.classes[c.id],
                          'opacity-0': !filters.classes[c.id]
                        })}
                      />
                      {c.name}
                    </div>
                    {c.status === 'archived' && <Icon icon='box-archive' className='ml-x1' />}
                  </DropdownItem>
                ))}
              </>
            )}
            {classesLoading && !toggleShowArchivedFirstTime && (
              <div className='mt-x2 flex justify-center'>
                <LoadingSpinner size={2} />
              </div>
            )}
          </>
          <ListGroupItem
            key='archived'
            className={classnames({
              'global-assignments__filters-clear-and-archived-toggle': isDraftsTab,
              'global-assignments__filters-archived-toggle': !isDraftsTab
            })}
          >
            {isDraftsTab && (
              <button
                className='global-assignments__filters-clear content-secondary'
                type='button'
                onClick={() => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    classes: {}
                  }));
                  if (setFiltersToUse) {
                    setFiltersToUse((prevFilters) => ({
                      ...prevFilters,
                      classes: {}
                    }));
                  }
                }}
              >
                Clear
              </button>
            )}
            {isDraftsTab ? (
              <div className='flex items-center justify-between'>
                <ArchivedFooter
                  showArchived={showArchived}
                  setClassesPage={setClassesPage}
                  setShowArchived={setShowArchived}
                  classesLoading={classesLoading}
                  toggleShowArchivedFirstTime={toggleShowArchivedFirstTime}
                  setToggleShowArchivedFirstTime={setToggleShowArchivedFirstTime}
                />
              </div>
            ) : (
              <ArchivedFooter
                showArchived={showArchived}
                setClassesPage={setClassesPage}
                setShowArchived={setShowArchived}
                classesLoading={classesLoading}
                toggleShowArchivedFirstTime={toggleShowArchivedFirstTime}
                setToggleShowArchivedFirstTime={setToggleShowArchivedFirstTime}
              />
            )}
          </ListGroupItem>
        </div>
      </Dropdown>
    </>
  );
};

export default ClassFilter;
